import React, { useEffect, useRef, useState } from "react";
import { Button, Row, Col, Popover, Modal } from "antd";
import {
  HeartOutlined,
  SoundOutlined,
  MutedOutlined,
} from "@ant-design/icons";

const BikeIcon = ({ size = 16, style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ verticalAlign: "-0.15em", ...style }}
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M7.5 16.5L10.2 9.8H13.5L15 13H10.9"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14.5 7.5H12.8"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle
      cx="6.5"
      cy="16.5"
      r="3.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
    <circle
      cx="17.5"
      cy="16.5"
      r="3.5"
      stroke="currentColor"
      strokeWidth="1.8"
    />
  </svg>
);

const HomeTrainerConnectControls = ({
  isMobile = false,
  isConnected,
  isBPMConnected,
  simulate,
  pause,
  isMute,
  onConnect,
  onDisconnect,
  connectToBPM,
  disconnectFromBPM,
  onToggleMute,
  connectionIssueDiv,
  showTroublePopover = false,
  controlHeight = 64,
  controlHeightMobile = 52,
}) => {
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [hadFtmsIssue, setHadFtmsIssue] = useState(false);
  const expandTimerRef = useRef(null);

  const height = isMobile ? controlHeightMobile : controlHeight;
  const trainerConnected = !!(simulate || isConnected);
  const hrConnected = !!(simulate || isBPMConnected);

  const openConnectModal = ({ ftmsIssue = false } = {}) => {
    setHadFtmsIssue(!!ftmsIssue);
    setConnectModalOpen(true);
  };

  const connectWithMode = async (mode) => {
    // Close immediately; the browser Bluetooth picker is the "loading UI".
    setConnectModalOpen(false);
    // If we're starting an FTMS attempt, schedule the *same* troubleshooting modal after 10s.
    // This cannot interrupt the browser picker, but it will be visible immediately after the picker closes.
    if (mode === "ftms") {
      try {
        expandTimerRef.current && clearTimeout(expandTimerRef.current);
      } catch {}
      expandTimerRef.current = setTimeout(() => {
        // Don't pop if already connected.
        if (trainerConnected) return;
        openConnectModal({ ftmsIssue: true });
      }, 15000);
    }
    try {
      // `onConnect` is expected to accept an optional options object.
      // mode: 'ftms' | 'readOnly'
      await onConnect?.({ mode });
      setHadFtmsIssue(false);
      setConnectModalOpen(false); // if a "10s" modal opened while waiting, close it on success
    } catch (e) {
      // Only open the "ERG not found? Expand search" modal for:
      // - cancelled picker cases
      // - the 10s timer case (handled above)
      if (mode === "ftms") {
        if (
          e?.name === "NotFoundError" ||
          e?.code === "FTMS_CANCELLED" ||
          e?.code === "EXPANDED_CANCELLED"
        ) {
          openConnectModal({ ftmsIssue: true });
        }
        return;
      } else {
        // Read-only failed/cancelled: keep it closed; user can click Connect again.
      }
    } finally {
      try {
        expandTimerRef.current && clearTimeout(expandTimerRef.current);
      } catch {}
      expandTimerRef.current = null;
    }
  };

  // If we become connected (or simulate), make sure the helper modal is closed.
  useEffect(() => {
    if (!trainerConnected) return;
    try {
      expandTimerRef.current && clearTimeout(expandTimerRef.current);
    } catch {}
    expandTimerRef.current = null;
    setConnectModalOpen(false);
    setHadFtmsIssue(false);
  }, [trainerConnected]);

  const trainerButton = (
    <Button
      id="tour-connect-trainer"
      block
      style={{ height }}
      className={`primary-glass-btn ${simulate || isConnected ? "redish-btn" : ""}`}
      onClick={isConnected || simulate ? onDisconnect : () => connectWithMode("ftms")}
      disabled={!pause}
      type="primary"
      title={trainerConnected ? "Disconnect trainer" : "Connect trainer"}
    >
      <BikeIcon style={{ marginRight: 4 }} />
      <span>{trainerConnected ? "Disconnect" : "Connect"}</span>
    </Button>
  );

  return (
    <div className="glass-panel glass-section">
      <Modal
        title={hadFtmsIssue ? "ERG not found?" : "Connect trainer"}
        open={connectModalOpen}
        onCancel={() => {
          setConnectModalOpen(false);
        }}
        rootClassName="liquid-glass-modal"
        footer={[
          <Button key="cancel" onClick={() => setConnectModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="expand"
            type="primary"
            className="primary-glass-btn"
            onClick={() => connectWithMode("readOnly")}
          >
            Expand search
          </Button>,
        ]}
      >
        <div style={{ whiteSpace: "pre-wrap" }}>
          {"No ERG/FTMS device selected.\n\n" +
            "Click “Expand search” to show other trainer profiles (power/cadence). You can still train, but ERG may not work.\n\n" +
            "Tip: many trainers show multiple Bluetooth names (ex: \"Trainer\" vs \"Trainer Power\")."}
        </div>
      </Modal>
      <Row gutter={8} align="middle">
        <Col flex="42%">
          {trainerConnected && !pause ? (
            <Popover
              title="Trainer connected"
              content="Pause the session to disconnect the trainer."
            >
              {trainerButton}
            </Popover>
          ) : showTroublePopover && !trainerConnected ? (
            <Popover
              title="Trouble Connecting Trainer to Computer?"
              content={connectionIssueDiv}
            >
              {trainerButton}
            </Popover>
          ) : (
            trainerButton
          )}
        </Col>

        <Col flex="42%">
          <Button
            id="tour-connect-hr"
            block
            style={{ height }}
            className={`primary-glass-btn ${simulate || isBPMConnected ? "redish-btn" : ""}`}
            onClick={isBPMConnected || simulate ? disconnectFromBPM : connectToBPM}
            disabled={!pause}
            type="primary"
            title={hrConnected ? "Disconnect heart-rate monitor" : "Connect heart-rate monitor"}
          >
            <HeartOutlined style={{ marginRight: 4 }} />
            <span>{hrConnected ? "Disconnect" : "Connect"}</span>
          </Button>
        </Col>

        <Col flex="16%">
          <Button
            id="tour-mute"
            block
            style={{ height }}
            className={`primary-glass-btn ${isMute ? "redish-btn" : ""}`}
            onClick={onToggleMute}
            type="primary"
            title={isMute ? "Muted (no beep)" : "Sound on (beep at interval end)"}
            aria-label={isMute ? "Muted (no beep)" : "Sound on (beep at interval end)"}
          >
            {isMute ? <MutedOutlined /> : <SoundOutlined />}
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default HomeTrainerConnectControls;


